/**
 * Public pay client for the checkout at `/pay` (`contracts/invoicing-pay.openapi.yaml`).
 *
 * ## Why this is not `src/lib/api.ts`
 *
 * Every authenticated page goes through `apiFetch` for a reason: it owns cookie
 * auth, CSRF, the correlation id and the 401/403 session probes. **This page
 * has no session and must not acquire one.** The payer is anonymous; the request
 * is authorised by the possession of the `payToken` in the path and by nothing
 * else. So this module is the deliberate exception the task allows:
 *
 *   - `credentials: "omit"` — the browser attaches no cookie, so a payer who
 *     happens to be signed in to Alloy in the same browser does not silently
 *     carry that session into the public surface, and no session cookie can
 *     ever be observed by the pay endpoints.
 *   - No `Authorization`, no `x-alloy-csrf-token`, and **no import of
 *     `src/lib/api.ts`** — importing it would pull `lib/step-up-resume` and
 *     `lib/session-expiry` into an anonymous page, and would make the
 *     authenticated request path reachable from here. The three shared helpers
 *     this module needs are re-derived locally (see `payIdempotencyKey`),
 *     except for support-reference extraction, which is imported from the
 *     framework-agnostic `@/i18n/support-reference` leaf module.
 *   - `referrerPolicy: "no-referrer"` on every request, matching the page's
 *     `<meta name="referrer">`, so the token in the path cannot ride out in a
 *     `Referer` header.
 *   - `redirect: "error"` — a redirect is refused rather than followed. The
 *     token lives in the request path, so following a redirect would hand it to
 *     whatever host the response names. A canonicalising redirect is a
 *     contract change, not something this client should paper over.
 *   - `cache: "no-store"` — a payment status is never served from a cache.
 *
 * ## Contract of every function here
 *
 *   - **Never throws.** A 4xx, a 5xx, a non-JSON body, an aborted request and a
 *     dead network all come back as the `{ok: false}` arm with a status (0 for
 *     "never reached the service").
 *   - **Never invents data.** A 2xx whose body does not map is
 *     `MALFORMED_RESPONSE`, not an empty object. Only a 204 counts as a
 *     bodyless success.
 *   - **Never logs the token.** The token is a bearer credential; it appears in
 *     a request path and in no message, error, telemetry string or console
 *     call. The unit suite asserts this by capturing every console method
 *     across a full scenario.
 *   - **Never mutates without an `Idempotency-Key`.** Every POST/PUT sends one,
 *     minted per user action, so a retry after a dropped connection replays the
 *     first result instead of issuing a second declaration or a second address.
 */

import { extractSupportInfo } from "@/i18n/support-reference";
import {
  mapAddress,
  mapCheckout,
  mapDeclaration,
  mapPayment,
  mapReceipt,
  mapRefund,
  type PayAddressView,
  type PayCheckoutView,
  type PayDeclarationInput,
  type PayDeclarationView,
  type PayPaymentView,
  type PayReceiptView,
  type PayRefundView,
} from "./pay-types";

/** The request never completed: offline, DNS, aborted, or a refused redirect. */
export const PAY_NETWORK_ERROR_CODE = "NETWORK_ERROR";

/** A 2xx whose body is not the shape the contract promises. */
export const PAY_MALFORMED_RESPONSE_CODE = "MALFORMED_RESPONSE";

/** Status reported when the request never reached the service. */
export const PAY_UNREACHED_STATUS = 0;

export interface PayError {
  /** Bounded service code, or one of the two client codes above. */
  reasonCode: string;
  /** Server-issued support reference (D636), display-only. */
  supportReference?: string;
  /** Missing capability named by a 403 body, display-only. */
  capability?: string;
}

export type PayResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: PayError; status: number };

export interface PayRequestOptions {
  signal?: AbortSignal;
  /**
   * Reuse one key across retries of the **same** user action; omit it and every
   * call mints its own, so two separate actions are never deduplicated into
   * one. The page mints one key per submit attempt and keeps it while the
   * attempt is in flight, which is the whole point of the header.
   */
  idempotencyKey?: string;
}

/* ------------------------------------------------------------ transport */

const PAY_BASE = "/v1/pay";

/**
 * The request path. The token is percent-encoded into a single path segment and
 * goes nowhere else — never a query string, never a header, never a body.
 */
function payPath(token: string, suffix = ""): string {
  return `${PAY_BASE}/${encodeURIComponent(token)}${suffix}`;
}

/** Absolute form of a same-origin path, so the request URL is unambiguous. */
function payUrl(path: string): string {
  const origin = typeof window !== "undefined" ? window.location?.origin : undefined;
  if (!origin || origin === "null") return path;
  return `${origin}${path}`;
}

/**
 * An `Idempotency-Key` for this page. Same shape and charset as the shared
 * helper (`^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$` — underscores rejected by the
 * gateway, ALL-4839), re-derived here so the public page does not import the
 * authenticated client module. Entropy is a v4 UUID; the key is a
 * deduplication envelope, never key material.
 */
export function payIdempotencyKey(scope = "pay"): string {
  const safeScope = scope.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "pay";
  const unique =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `idem.sandbox.ui.${safeScope}.${unique}`;
}

function boundedText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, 160) : undefined;
}

/** The refusal body: the contract's `code`, plus the two gateway spellings. */
function problemError(res: Response, data: unknown): PayError {
  const body = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
  const reasonCode =
    boundedText(body.code)
    ?? boundedText(body.reason_code)
    ?? boundedText(body.reasonCode)
    ?? `HTTP_${res.status}`;
  const support = extractSupportInfo({ response: res, body });
  return {
    reasonCode,
    supportReference: support.supportReference ?? undefined,
    capability: support.capability ?? undefined,
  };
}

/** Reads a body without throwing: a refusal can be HTML, and 204 has none. */
async function readBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch {
    return null;
  }
}

interface Outcome {
  ok: boolean;
  status: number;
  data: unknown;
  etag: string | null;
  error?: PayError;
}

async function call(path: string, init: RequestInit, scope: string, options: PayRequestOptions): Promise<Outcome> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body !== undefined) headers.set("content-type", "application/json");
  if (init.method && init.method !== "GET") {
    headers.set("idempotency-key", options.idempotencyKey ?? payIdempotencyKey(scope));
  }

  try {
    const res = await fetch(payUrl(path), {
      ...init,
      headers,
      signal: options.signal,
      // No session, ever: see the module docblock.
      credentials: "omit",
      cache: "no-store",
      referrerPolicy: "no-referrer",
      // A redirect would carry the token in the path to another host.
      redirect: "error",
    });
    const data = await readBody(res);
    const etag = res.headers.get("etag");
    if (!res.ok) return { ok: false, status: res.status, data, etag, error: problemError(res, data) };
    return { ok: true, status: res.status, data, etag };
  } catch {
    // Never attach the URL: it contains the token.
    return { ok: false, status: PAY_UNREACHED_STATUS, data: null, etag: null, error: { reasonCode: PAY_NETWORK_ERROR_CODE } };
  }
}

/** A mapped single resource, or `MALFORMED_RESPONSE` when the body is not one. */
function mapOrFail<T>(outcome: Outcome, map: (data: unknown) => T | null): PayResult<T> {
  if (!outcome.ok) return { ok: false, error: outcome.error ?? { reasonCode: `HTTP_${outcome.status}` }, status: outcome.status };
  const mapped = map(outcome.data);
  if (mapped === null) {
    return { ok: false, error: { reasonCode: PAY_MALFORMED_RESPONSE_CODE }, status: outcome.status };
  }
  return { ok: true, data: mapped };
}

/* ------------------------------------------------------------ operations */

/**
 * `GET /v1/pay/{payToken}` — the checkout: issuer legal name, invoice summary,
 * certified networks, the payer's existing declaration and the payment state.
 */
export async function readCheckout(token: string, options: PayRequestOptions = {}): Promise<PayResult<PayCheckoutView>> {
  return mapOrFail(await call(payPath(token), { method: "GET" }, "pay-read", options), mapCheckout);
}

/**
 * `PUT /v1/pay/{payToken}/declaration` — the payer's legal name, country and
 * contact email. Idempotent: the same key replays the same declaration, and the
 * service refuses a later edit with `DECLARATION_FROZEN`.
 */
export async function saveDeclaration(
  token: string,
  declaration: PayDeclarationInput,
  options: PayRequestOptions = {},
): Promise<PayResult<PayDeclarationView>> {
  return mapOrFail(
    await call(
      payPath(token, "/declaration"),
      { method: "PUT", body: JSON.stringify(declaration) },
      "pay-declaration",
      options,
    ),
    mapDeclaration,
  );
}

/**
 * `POST /v1/pay/{payToken}/addresses` — the unique deposit address for this
 * invoice on the chosen network, with the attestation the gate verifies.
 *
 * The response is returned **unverified**: nothing here decides whether the
 * address may be shown. That decision belongs to `attestation.ts`, and the page
 * renders the address only when that gate says yes. Keeping the two apart is
 * what makes "the address is never shown without a passing check" a property of
 * one code path instead of a property of every caller.
 */
export async function requestAddress(
  token: string,
  network: string,
  options: PayRequestOptions = {},
): Promise<PayResult<PayAddressView>> {
  return mapOrFail(
    await call(
      payPath(token, "/addresses"),
      { method: "POST", body: JSON.stringify({ network }) },
      "pay-address",
      options,
    ),
    mapAddress,
  );
}

/**
 * The status poll. Three outcomes, and the third is not an error:
 *
 *   - `changed` — a new status, with the `ETag` to send next time;
 *   - `unchanged` — the service answered `304`, so the last known status stands;
 *   - `{ok: false}` — a refusal or a transport failure, which the poll counts
 *     toward its bounded retry budget rather than surfacing as a page error.
 */
export type PayStatusResult =
  | { ok: true; changed: true; payment: PayPaymentView; etag: string | null }
  | { ok: true; changed: false }
  | { ok: false; error: PayError; status: number };

/** `GET /v1/pay/{payToken}/status`, conditional on the last `ETag`. */
export async function readStatus(
  token: string,
  options: PayRequestOptions & { etag?: string | null } = {},
): Promise<PayStatusResult> {
  const headers = new Headers();
  if (options.etag) headers.set("if-none-match", options.etag);

  const outcome = await call(payPath(token, "/status"), { method: "GET", headers }, "pay-status", options);
  if (outcome.status === 304) return { ok: true, changed: false };
  if (!outcome.ok) {
    return { ok: false, error: outcome.error ?? { reasonCode: `HTTP_${outcome.status}` }, status: outcome.status };
  }
  const payment = mapPayment(outcome.data);
  if (payment === null) {
    return { ok: false, error: { reasonCode: PAY_MALFORMED_RESPONSE_CODE }, status: outcome.status };
  }
  return { ok: true, changed: true, payment, etag: outcome.etag };
}

/**
 * `GET /v1/pay/{payToken}/receipt`. The body names a same-origin download path;
 * an absolute or off-origin URL is dropped by the mapper rather than linked.
 */
export async function readReceipt(token: string, options: PayRequestOptions = {}): Promise<PayResult<PayReceiptView>> {
  return mapOrFail(await call(payPath(token, "/receipt"), { method: "GET" }, "pay-receipt", options), mapReceipt);
}

/**
 * `POST /v1/pay/{payToken}/refunds/{refundId}/address` — the address the refund
 * should go to. The service emails a one-time code to the payer's declared
 * contact address; the address is not used until `confirmRefundAddress` succeeds.
 */
export async function submitRefundAddress(
  token: string,
  refundId: string,
  address: string,
  options: PayRequestOptions = {},
): Promise<PayResult<PayRefundView>> {
  return mapOrFail(
    await call(
      payPath(token, `/refunds/${encodeURIComponent(refundId)}/address`),
      { method: "POST", body: JSON.stringify({ address }) },
      "pay-refund-address",
      options,
    ),
    mapRefund,
  );
}

/**
 * `POST /v1/pay/{payToken}/refunds/{refundId}/address:confirm` — the one-time
 * code from the payer's email. The code is a secret: it is sent in the body and
 * is never logged, never echoed back into the page, and never placed in a URL.
 */
export async function confirmRefundAddress(
  token: string,
  refundId: string,
  code: string,
  options: PayRequestOptions = {},
): Promise<PayResult<PayRefundView>> {
  return mapOrFail(
    await call(
      payPath(token, `/refunds/${encodeURIComponent(refundId)}/address:confirm`),
      { method: "POST", body: JSON.stringify({ code }) },
      "pay-refund-confirm",
      options,
    ),
    mapRefund,
  );
}

/* --------------------------------------------------------- refusal shape */

/**
 * How the payer-facing page reads a refusal to its first read of the link.
 *
 * The classification is the contract's, and two rules in it are load-bearing:
 *
 *   - **A not-active link and a rate limit are different states with different
 *     copy, but neither reveals whether the token exists** (spec lines 261-264).
 *     Everything unrecognised that is not clearly an outage therefore lands in
 *     `not-active` — the fail-closed reading.
 *   - **A revoked token is not-active, not closed.** "Unknown, revoked and
 *     malformed tokens all show the identical page"; only an expired or void
 *     *invoice* shows "no longer accepting payments".
 */
export type CheckoutRefusal =
  | { kind: "not-active" }
  | { kind: "closed" }
  | { kind: "rate-limited" }
  | { kind: "denied"; error: PayError }
  | { kind: "error"; error: PayError; status: number };

/** Codes that mean the link itself is not usable. */
const NOT_ACTIVE_CODES = new Set([
  "PAY_LINK_NOT_ACTIVE",
  "PAY_LINK_NOT_FOUND",
  "PAY_LINK_REVOKED",
  "PAY_LINK_INVALID",
  "PAY_TOKEN_INVALID",
  "PAY_TOKEN_REVOKED",
  "TOKEN_INVALID",
  "NOT_FOUND",
  "VALIDATION_FAILED",
]);

/** Codes that mean the invoice is closed — the link is fine, the invoice is not. */
const CLOSED_CODES = new Set([
  "INVOICE_CLOSED",
  "INVOICE_EXPIRED",
  "INVOICE_VOID",
  "INVOICE_NOT_ACCEPTING_PAYMENTS",
  "PAY_LINK_EXPIRED",
  "PAYMENT_WINDOW_CLOSED",
]);

/**
 * Classifies a failed first read. Nothing here schedules a retry from a
 * server-supplied hint: the page offers a single "try again", which is the
 * payer's decision, and never a client-side loop against a refusal.
 */
export function classifyCheckoutFailure(error: PayError, status: number): CheckoutRefusal {
  const code = error.reasonCode;

  if (status === PAY_UNREACHED_STATUS || code === PAY_NETWORK_ERROR_CODE) {
    return { kind: "error", error, status };
  }
  if (status === 429 || code === "RATE_LIMITED" || code === "TOO_MANY_REQUESTS") {
    return { kind: "rate-limited" };
  }
  if (status === 403 || code === "FORBIDDEN") return { kind: "denied", error };
  if (status >= 500) return { kind: "error", error, status };
  if (CLOSED_CODES.has(code)) return { kind: "closed" };
  if (NOT_ACTIVE_CODES.has(code)) return { kind: "not-active" };

  // Any other 4xx is a refusal this build does not know. Fail closed and say
  // nothing about whether the token exists.
  return status >= 400 && status < 500 ? { kind: "not-active" } : { kind: "error", error, status };
}
